import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Paper,
    Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PreviewIcon from '@mui/icons-material/Preview';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FgsManagement } from '../../controllers';
import { TokenValidator } from '../../utils';
import PrintRouting from './printRouting';
import ShippingOverview from './shippingOverview';
import NewClientSetup from './newClientSetup';

const emptyForm = {client: '', product: '', code: '', template: '', additionalLabel: false, active: true, reason: ''};
const correlationId = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : ((r & 3) | 8)).toString(16);
    });
const displayTime = value => value ? new Date(value).toLocaleString() : 'Legacy / not audited';

export default function Main() {
    const [data, setData] = React.useState({rows: [], clients: [], products: [], templates: []});
    const [filters, setFilters] = React.useState({client: '', fgs: '', template: '', status: 'all'});
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [editor, setEditor] = React.useState(null);
    const [form, setForm] = React.useState(emptyForm);
    const [statusDialog, setStatusDialog] = React.useState(null);
    const [history, setHistory] = React.useState(null);
    const [preview, setPreview] = React.useState(null);
    const [tab, setTab] = React.useState(0);

    const token = () => TokenValidator.GetInternalUser();

    const load = React.useCallback(async () => {
        setLoading(true);
        const response = await FgsManagement.List(await token());
        setLoading(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to load FGS configurations'});
            return;
        }
        setData(response.result);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const rows = React.useMemo(() => data.rows.filter(row => {
        if (filters.client && !row.client.toLowerCase().includes(filters.client.toLowerCase())) return false;
        if (filters.fgs && !row.code.toLowerCase().includes(filters.fgs.toLowerCase())) return false;
        if (filters.template && row.template !== filters.template) return false;
        if (filters.status === 'active' && !row.active) return false;
        if (filters.status === 'inactive' && row.active) return false;
        return true;
    }), [data.rows, filters]);

    const openCreate = () => {
        setForm({...emptyForm});
        setEditor({mode: 'create'});
    };

    const openEdit = row => {
        setForm({
            client: row.client, product: row.product, code: row.code, template: row.template,
            additionalLabel: row.additionalLabel, active: row.active, reason: ''
        });
        setEditor({mode: 'edit', row});
    };

    const validate = () => {
        if (!form.client || !form.product || !form.code || !form.template) return 'Client, product, FGS code and template are required.';
        if (form.reason.trim().length < 3) return 'A reason of at least 3 characters is required.';
        return '';
    };

    const save = async () => {
        const error = validate();
        if (error) { setMessage({severity: 'error', text: error}); return; }
        setBusy(true);
        const payload = {...form, correlationId: correlationId()};
        const response = editor.mode === 'create'
            ? await FgsManagement.Create(await token(), payload)
            : await FgsManagement.Update(await token(), editor.row.id, {
                code: form.code, template: form.template, additionalLabel: form.additionalLabel,
                reason: form.reason, correlationId: payload.correlationId
            });
        setBusy(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to save FGS configuration'});
            return;
        }
        setEditor(null);
        setMessage({severity: 'success', text: editor.mode === 'create' ? 'FGS configuration created.' : 'FGS configuration updated.'});
        await load();
    };

    const changeStatus = async () => {
        if (statusDialog.reason.trim().length < 3) {
            setMessage({severity: 'error', text: 'A reason of at least 3 characters is required.'});
            return;
        }
        setBusy(true);
        const payload = {reason: statusDialog.reason, correlationId: correlationId()};
        const response = statusDialog.row.active
            ? await FgsManagement.Deactivate(await token(), statusDialog.row.id, payload)
            : await FgsManagement.Activate(await token(), statusDialog.row.id, payload);
        setBusy(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to change status'});
            return;
        }
        setStatusDialog(null);
        setMessage({severity: 'success', text: 'FGS status updated.'});
        await load();
    };

    const showHistory = async row => {
        setBusy(true);
        const response = await FgsManagement.History(await token(), row.id);
        setBusy(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to load history'});
            return;
        }
        setHistory({row, entries: response.result});
    };

    const showPreview = async row => {
        setBusy(true);
        const response = await FgsManagement.Preview(await token(), {code: row.code, template: row.template});
        setBusy(false);
        if (!response || !response.success) {
            setMessage({severity: 'error', text: response ? response.message : 'Unable to preview FGS'});
            return;
        }
        setPreview({row, ...response.result});
    };

    return (
        <Box sx={{p: 3}}>
            <Stack direction={{xs: 'column', md: 'row'}} justifyContent='space-between' gap={2} sx={{mb: 2}}>
                <Box>
                    <Typography variant='h4'>FGS Management</Typography>
                    <Typography color='text.secondary'>Manage client FGS configurations</Typography>
                </Box>
                <Stack direction='row' gap={1} alignItems='center'>
                    <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy}>Refresh</Button>
                    {tab === 0 && <Button variant='contained' startIcon={<AddIcon />} onClick={openCreate} disabled={busy}>Create assignment</Button>}
                </Stack>
            </Stack>

            <Paper sx={{mb: 2}}><Tabs value={tab} onChange={(_, value) => setTab(value)} variant='scrollable' scrollButtons='auto'>
                <Tab label='FGS Assignments' />
                <Tab label='Print Routing' />
                <Tab label='Shipping' />
                <Tab label='New Client Setup' />
            </Tabs></Paper>

            {tab === 0 && <>
            {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{mb: 2}}>{message.text}</Alert>}
            <Alert severity='warning' sx={{mb: 2}}>
                Changes affect future prints and current Manual Reprint resolution. Stored label rows are not modified.
            </Alert>

            <Paper sx={{p: 2, mb: 2}}>
                <Stack direction={{xs: 'column', md: 'row'}} gap={2}>
                    <TextField size='small' label='Client' value={filters.client} onChange={e => setFilters({...filters, client: e.target.value})} />
                    <TextField size='small' label='FGS' value={filters.fgs} onChange={e => setFilters({...filters, fgs: e.target.value})} />
                    <TextField select size='small' label='Template' value={filters.template} sx={{minWidth: 220}}
                        onChange={e => setFilters({...filters, template: e.target.value})}>
                        <MenuItem value=''>All</MenuItem>
                        {data.templates.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                    <TextField select size='small' label='Status' value={filters.status} sx={{minWidth: 130}}
                        onChange={e => setFilters({...filters, status: e.target.value})}>
                        <MenuItem value='all'>All</MenuItem><MenuItem value='active'>Active</MenuItem><MenuItem value='inactive'>Inactive</MenuItem>
                    </TextField>
                </Stack>
            </Paper>

            <TableContainer component={Paper}>
                <Table size='small'>
                    <TableHead><TableRow>
                        <TableCell>Client</TableCell><TableCell>Company ID</TableCell><TableCell>Product / condition</TableCell>
                        <TableCell>FGS code</TableCell><TableCell>ZPL template</TableCell><TableCell>Additional label</TableCell>
                        <TableCell>Status</TableCell><TableCell>Last modified</TableCell><TableCell align='right'>Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                        {loading && <TableRow><TableCell colSpan={9} align='center'><CircularProgress size={28} /></TableCell></TableRow>}
                        {!loading && rows.map(row => <TableRow key={row.key} hover>
                            <TableCell>{row.client}{row.inconsistent && <Chip size='small' color='warning' label='Alias mismatch' sx={{ml: 1}} />}</TableCell>
                            <TableCell>{row.companyIds.length ? row.companyIds.join(', ') : 'Not linked'}</TableCell>
                            <TableCell>{row.product}</TableCell><TableCell>FGS-{row.code}</TableCell><TableCell>{row.template || 'Unassigned'}</TableCell>
                            <TableCell>{row.additionalLabel ? 'Yes' : 'No'}</TableCell>
                            <TableCell><Chip size='small' color={row.active ? 'success' : 'default'} label={row.active ? 'Active' : 'Inactive'} /></TableCell>
                            <TableCell>{displayTime(row.lastModified)}</TableCell>
                            <TableCell align='right'>
                                <Tooltip title='Preview'><IconButton onClick={() => showPreview(row)} disabled={busy}><PreviewIcon /></IconButton></Tooltip>
                                <Tooltip title='Edit'><IconButton onClick={() => openEdit(row)} disabled={busy}><EditIcon /></IconButton></Tooltip>
                                <Tooltip title='History'><IconButton onClick={() => showHistory(row)} disabled={busy}><HistoryIcon /></IconButton></Tooltip>
                                <Tooltip title={row.active ? 'Deactivate' : 'Activate'}><IconButton color={row.active ? 'warning' : 'success'}
                                    onClick={() => setStatusDialog({row, reason: ''})} disabled={busy}><PowerSettingsNewIcon /></IconButton></Tooltip>
                            </TableCell>
                        </TableRow>)}
                        {!loading && !rows.length && <TableRow><TableCell colSpan={9} align='center'>No matching FGS configurations.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={editor !== null} onClose={() => !busy && setEditor(null)} maxWidth='sm' fullWidth>
                <DialogTitle>{editor && editor.mode === 'create' ? 'Create FGS assignment' : 'Edit FGS assignment'}</DialogTitle>
                <DialogContent><Stack gap={2} sx={{pt: 1}}>
                    <Autocomplete freeSolo options={data.clients.map(item => item.name)} value={form.client || null}
                        inputValue={form.client} disabled={editor && editor.mode === 'edit'}
                        onChange={(_, value) => setForm({...form, client: value || ''})}
                        onInputChange={(_, value) => setForm({...form, client: value})}
                        renderInput={params => <TextField {...params} label='Client' inputProps={{...params.inputProps, maxLength: 100}}
                            helperText="Select an observed client or type a new one. The name must match the future order's case_client." />} />
                    <TextField select label='Product / condition' value={form.product} disabled={editor && editor.mode === 'edit'}
                        onChange={e => setForm({...form, product: e.target.value})}>
                        {data.products.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                    <TextField label='FGS code' value={form.code} inputProps={{maxLength: 40}}
                        helperText='The optional FGS- prefix is normalized by the backend.' onChange={e => setForm({...form, code: e.target.value})} />
                    <TextField select label='ZPL template' value={form.template} onChange={e => setForm({...form, template: e.target.value})}>
                        {data.templates.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                    </TextField>
                    <FormControlLabel control={<Checkbox checked={form.additionalLabel}
                        onChange={e => setForm({...form, additionalLabel: e.target.checked})} />} label='Print additional FGS label at bagging (client-wide)' />
                    {editor && editor.mode === 'create' && <FormControlLabel control={<Checkbox checked={form.active}
                        onChange={e => setForm({...form, active: e.target.checked})} />} label='Active' />}
                    <TextField required multiline minRows={2} label='Reason' value={form.reason} inputProps={{maxLength: 500}}
                        onChange={e => setForm({...form, reason: e.target.value})} />
                    <Alert severity='info'>Current values remain in immutable audit history. Future prints may use the new value.</Alert>
                </Stack></DialogContent>
                <DialogActions><Button onClick={() => setEditor(null)} disabled={busy}>Cancel</Button><Button variant='contained' onClick={save} disabled={busy}>Save</Button></DialogActions>
            </Dialog>

            <Dialog open={statusDialog !== null} onClose={() => !busy && setStatusDialog(null)} maxWidth='sm' fullWidth>
                <DialogTitle>{statusDialog && statusDialog.row.active ? 'Deactivate FGS assignment' : 'Activate FGS assignment'}</DialogTitle>
                <DialogContent><Stack gap={2} sx={{pt: 1}}>
                    {statusDialog && <Typography>{statusDialog.row.client} / {statusDialog.row.product} / FGS-{statusDialog.row.code}</Typography>}
                    <Alert severity='warning'>Deactivation blocks this assignment from future deterministic FGS/ZPL lookup. It does not alter stored labels.</Alert>
                    <TextField required multiline minRows={2} label='Reason' value={statusDialog ? statusDialog.reason : ''}
                        inputProps={{maxLength: 500}} onChange={e => setStatusDialog({...statusDialog, reason: e.target.value})} />
                </Stack></DialogContent>
                <DialogActions><Button onClick={() => setStatusDialog(null)} disabled={busy}>Cancel</Button><Button color='warning' variant='contained' onClick={changeStatus} disabled={busy}>Confirm</Button></DialogActions>
            </Dialog>

            <Dialog open={history !== null} onClose={() => setHistory(null)} maxWidth='lg' fullWidth>
                <DialogTitle>Change history</DialogTitle><DialogContent>
                    {history && !history.entries.length && <Alert severity='info'>No audited changes. This is a legacy configuration.</Alert>}
                    {history && history.entries.map((entry, index) => <Paper key={`${entry.correlation_id}-${index}`} variant='outlined' sx={{p: 2, mb: 1}}>
                        <Stack direction={{xs: 'column', md: 'row'}} gap={2} justifyContent='space-between'>
                            <Typography><strong>{entry.action}</strong> — {entry.reason}</Typography>
                            <Typography color='text.secondary'>{displayTime(entry.created_at)} / {entry.actor || 'Deleted user'}</Typography>
                        </Stack>
                        <Typography variant='caption'>Correlation: {entry.correlation_id}</Typography>
                    </Paper>)}
                </DialogContent><DialogActions><Button onClick={() => setHistory(null)}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={preview !== null} onClose={() => setPreview(null)} maxWidth='sm' fullWidth>
                <DialogTitle>FGS preview (no printing)</DialogTitle><DialogContent>
                    {preview && <Stack gap={2} sx={{pt: 1}}>
                        <TextField label='Template' value={preview.template} InputProps={{readOnly: true}} />
                        <TextField label='Visible FGS' value={preview.fgsText} InputProps={{readOnly: true}} />
                        <TextField label='QR segment appended at print time' value={preview.qrSegment} InputProps={{readOnly: true}} />
                        <Alert severity='success'>Preview is read-only and did not contact a printer.</Alert>
                    </Stack>}
                </DialogContent><DialogActions><Button onClick={() => setPreview(null)}>Close</Button></DialogActions>
            </Dialog>
            </>}
            {tab === 1 && <PrintRouting />}
            {tab === 2 && <ShippingOverview />}
            {tab === 3 && <NewClientSetup />}
        </Box>
    );
}
